/**
 * notificationSound — emite um "pop" curto via Web Audio API quando
 * chega uma mensagem nova. Som sintético pra não depender de arquivo
 * de áudio embarcado.
 *
 * Política (igual WhatsApp Web):
 *   - Toca quando mensagem entra (direcao=entrada) E a conversa NÃO
 *     está aberta (ou o usuário está em outra aba).
 *   - Não toca se o usuário deu mute (toggle persistido em localStorage).
 *
 * Browsers bloqueiam AudioContext até a primeira interação do usuário —
 * por isso só criamos o contexto lazy, no primeiro `play()`. Se o
 * navegador bloqueia mesmo assim, falha silenciosamente (não rejeita
 * promise, não loga warning ruidoso).
 */

const STORAGE_KEY_MUTE = 'atendimento-som-mute';

let audioContext = null;
let ultimoToqueEm = 0;
// Throttle pra não estourar som se chegar uma rajada de mensagens
// (ex.: contato mandando 10 mensagens em sequência).
const COOLDOWN_MS = 800;

function getContext() {
  if (audioContext) return audioContext;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    audioContext = new Ctx();
    return audioContext;
  } catch (_e) {
    return null;
  }
}

function isMuted() {
  try { return window.localStorage.getItem(STORAGE_KEY_MUTE) === '1'; }
  catch (_e) { return false; }
}

function setMuted(valor) {
  try { window.localStorage.setItem(STORAGE_KEY_MUTE, valor ? '1' : '0'); }
  catch (_e) { /* ignore */ }
}

/**
 * Toca um "ding-dong" curto estilo notificação: duas notas em terça
 * maior (G5 → B5) em sucessão, com sustain harmônico discreto pra dar
 * corpo. Total ~450ms.
 */
function playNova() {
  if (isMuted()) return;
  const agora = Date.now();
  if (agora - ultimoToqueEm < COOLDOWN_MS) return;

  const ctx = getContext();
  if (!ctx) return;

  // Se o contexto foi suspenso (autoplay policy), tenta resumir.
  if (ctx.state === 'suspended') {
    try { ctx.resume(); } catch (_e) { return; }
  }

  try {
    const t0 = ctx.currentTime;

    // Master gain pra controlar volume final num só lugar.
    const master = ctx.createGain();
    master.gain.value = 0.55;
    master.connect(ctx.destination);

    // --- Nota 1 (G5 = 783.99 Hz) ---
    tocarNota(ctx, master, {
      inicio: t0,
      freq: 783.99,
      duracao: 0.32,
      pico: 0.45,
      ataque: 0.012,
      decay: 0.30,
    });

    // --- Nota 2 (B5 = 987.77 Hz), 120ms depois — "ding-dong" ---
    tocarNota(ctx, master, {
      inicio: t0 + 0.12,
      freq: 987.77,
      duracao: 0.34,
      pico: 0.50,
      ataque: 0.012,
      decay: 0.32,
    });

    // Harmônico sutil (quinta acima da segunda nota — D6 = 1174.66Hz)
    // pra dar "shimmer" e parecer menos elétrico. Volume baixo (1/3 do pico).
    tocarNota(ctx, master, {
      inicio: t0 + 0.13,
      freq: 1174.66,
      duracao: 0.30,
      pico: 0.18,
      ataque: 0.02,
      decay: 0.28,
      tipo: 'triangle',
    });

    ultimoToqueEm = agora;
  } catch (_e) { /* silencioso — som é cosmético */ }
}

/**
 * Cria um oscilador + envelope ADSR simples e conecta no master.
 * Auto-cleanup: oscilador para sozinho ao fim do decay.
 */
function tocarNota(ctx, master, opts) {
  const {
    inicio, freq, duracao, pico, ataque, decay, tipo = 'sine',
  } = opts;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = tipo;
  osc.frequency.setValueAtTime(freq, inicio);

  // Envelope: silencio → pico rápido → decay exponencial.
  gain.gain.setValueAtTime(0.0001, inicio);
  gain.gain.exponentialRampToValueAtTime(pico, inicio + ataque);
  gain.gain.exponentialRampToValueAtTime(0.0001, inicio + ataque + decay);

  osc.connect(gain);
  gain.connect(master);
  osc.start(inicio);
  osc.stop(inicio + duracao + 0.02);
}

export default {
  playNova,
  isMuted,
  setMuted,
};
